import moment from "moment";
import {
  Text,
  Group,
  Badge,
  Card,
  Loader,
  Tabs,
  Button,
  Menu,
  ActionIcon,
  NumberInput,
  Tooltip,
} from "@mantine/core";
import DataTable from "./dataTable";
import { useLocation } from "react-router-dom";
import { useAuthUser } from "react-auth-kit";

import { IconDotsVertical, IconRefresh } from "@tabler/icons";
import { showNotification } from "@mantine/notifications";
import { getYearColumnDefs } from "../helpers/commissionScheduleYears";
import { useTabNavigate } from "../hooks/useTabNavigate";
import { resolveEntityPath } from "../utils/resolveEntityPath";
import { getEntityTabLabel } from "../utils/tabNavigation";

const BadgeWithTooltip = ({ label, children }) => {
  if (!label) return children;

  return (
    <Tooltip label={label} withArrow openDelay={300}>
      <span style={{ display: "inline-flex" }}>{children}</span>
    </Tooltip>
  );
};

const CommissionSettingScheduleCard = ({
  commissionSetting,
  product = null,
  rateFactor = 1,
  mode = "view",
  isCertain = false,
  onRefresh = () => {},
  onReset = () => {},
  onCustomize = () => {},
}) => {
  const tabNav = useTabNavigate();
  const location = useLocation();
  const auth = useAuthUser();
  const userRole = auth()?.userRole;
  const { row, productSchedule } = commissionSetting ?? {};
  const { grossRate, conditions, isCustomized } = row ?? {};
  const commissionSchedule = productSchedule?.commissionSchedule;
  const productId = product?._id ?? product ?? productSchedule?.product?._id ?? productSchedule?.product;
  const productCode =
    product?.code ?? productSchedule?.planCode ?? productSchedule?.productName;

  const openCommissionSchedule = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!commissionSchedule?._id) return;
    tabNav.openDetail({
      pathname: "/management/commissionSchedule",
      id: commissionSchedule._id,
      mode: "edit",
      label: commissionSchedule.code,
    });
  };

  const openProduct = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!productId) return;

    const pathname = resolveEntityPath("product", userRole, location.pathname);
    if (!pathname) return;

    tabNav.openDetail({
      pathname,
      id: productId,
      mode: "edit",
      label: getEntityTabLabel(product, productId, "product"),
    });
  };

  const getRate = (rate) => {
    return (rate * 100 * rateFactor).toFixed(2);
  };
  const data = [
    {
      grossRate: grossRate,
    },
  ];
  const columns = getYearColumnDefs().map(({ key, label }) => ({
    field: `grossRate.${key}`,
    headerName: label,
    cellRender: (col, data) => {
      return mode == "edit" ? (
        <NumberInput
          size="xs"
          value={+getRate(data.grossRate?.[key])}
          suffix="%"
          precision={2}
          onChange={(value) => {
            data.grossRate[key] = value / (100 * rateFactor);
          }}
        />
      ) : (
        <Text
          size="xs"
          color={key === "yr1" && !data.grossRate?.[key] ? "red" : "inherit"}
        >
          {getRate(data.grossRate?.[key] ?? 0)}%
        </Text>
      );
    },
  }));

  return (
    <>
      {/* {isCustomized ? "Customize" : "default"} */}
      {/* <ReactJson
        name="commissionSetting"
        src={commissionSetting}
        style={{ backgroundColor: "white" }}
      /> */}
      <Group position="apart" mt="md" mb="md">
        <Group spacing={2}>
          <BadgeWithTooltip
            label={
              isCertain
                ? "Certain payment — premium and payment details are confirmed"
                : "Uncertain payment — premium or payment details are not yet confirmed"
            }
          >
            <Badge size="xs" color={isCertain ? "green" : "red"} radius="xs">
              {isCertain ? "Certain" : "UnCertain"}
            </Badge>
          </BadgeWithTooltip>
          {!isCustomized && productCode && (
            <BadgeWithTooltip
              label={
                productId
                  ? "Product code — click to open the product"
                  : "Product code for this order"
              }
            >
              <Badge
                size="xs"
                color="teal"
                radius="xs"
                sx={productId ? { cursor: "pointer" } : undefined}
                onClick={productId ? openProduct : undefined}
              >
                {productCode}
              </Badge>
            </BadgeWithTooltip>
          )}
          {!isCustomized && commissionSchedule?.code && (
            <BadgeWithTooltip label="Commission schedule — click to open the schedule">
              <Badge
                size="xs"
                color="violet"
                radius="xs"
                sx={{ cursor: "pointer" }}
                onClick={openCommissionSchedule}
              >
                {commissionSchedule.code}
              </Badge>
            </BadgeWithTooltip>
          )}
          {!isCustomized && productSchedule?.code && (
            <BadgeWithTooltip label="Product commission schedule version effective for this order's signed date">
              <Badge size="xs" color="blue" radius="xs">
                {productSchedule.code}
              </Badge>
            </BadgeWithTooltip>
          )}
          {!isCustomized && !productSchedule && (
            <BadgeWithTooltip label="No product commission schedule found for this product and signed date">
              <Badge size="xs" color="red" radius="xs">
                No Product Schedule found
              </Badge>
            </BadgeWithTooltip>
          )}

          {!isCustomized && (
            <Group spacing={2}>
              {conditions?.map((condition, index) => (
                <BadgeWithTooltip
                  key={`${condition}-${index}`}
                  label="Matched commission row condition"
                >
                  <Badge size="xs" color="blue" radius="xs">
                    {condition}
                  </Badge>
                </BadgeWithTooltip>
              ))}
            </Group>
          )}
          {!isCustomized && !row && (
            <Group spacing={2}>
              <BadgeWithTooltip label="No commission table row matched the order's payment term, age, and other conditions">
                <Badge size="xs" color="red" radius="xs">
                  No Commission Row matched
                </Badge>
              </BadgeWithTooltip>
            </Group>
          )}

          {isCustomized && (
            <BadgeWithTooltip label="Commission schedule has been manually customized for this order">
              <Badge size="xs" color="red" radius="xs">
                Customized
              </Badge>
            </BadgeWithTooltip>
          )}
        </Group>

        <Group>
          <ActionIcon onClick={onRefresh} size="xs">
            <IconRefresh size={16} aria-label="Refresh" />
          </ActionIcon>
          <Menu position="bottom-end" withinPortal size="xs">
            <Menu.Target>
              <ActionIcon>
                <IconDotsVertical size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item onClick={onReset} py={5}>
                <Text size="xs">Reset Schedule</Text>
              </Menu.Item>
              <Menu.Item onClick={onCustomize} py={5}>
                <Text size="xs">Customize Schedule</Text>
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Group>

      {commissionSetting && (
        <Card withBorder mb="md">
          <DataTable
            columns={columns}
            data={data}
            hidePagination={true}
            hideSearchBar={true}
            padding="xs"
            tdPaddingX={"2px"}
          />
          <Group mt={0} spacing={"xl"}>
            <Text size="xs" mt="xs" color="dimmed">
              {productSchedule?.effectiveDate &&
                `Effective: ${moment(productSchedule?.effectiveDate).format(
                  "YYYY-MM-DD"
                )}`}
            </Text>
            <Text size="xs" mt="xs" color="dimmed">
              {productSchedule?.endDate &&
                ` Expired: ${moment(productSchedule?.endDate).format(
                  "YYYY-MM-DD"
                )}`}
            </Text>
          </Group>
        </Card>
      )}
      {!productSchedule && (
        <Card withBorder mb="md">
          <Text size="xs" color="dimmed">
            No Commission Schedule found
          </Text>
        </Card>
      )}
    </>
  );
};

export default CommissionSettingScheduleCard;
